import dayjs from 'dayjs';
import boxx from '@baidu/boxx';
import {
    CERTIFICATE,
    ChangeStateKey,
    ChangeTypeKey,
    CHANGE_STATE,
    CHANGE_TYPE,
    HOME_PAGE,
    IMAGE_URL,
    LICENSE_IMG_URL,
    QUALIFY_CHANGE_STATE,
    USER_CENTER,
    H5_TICKET_URL,
    PC_TICKET_CREATE,
    ORGANIZATION_TYPE,
} from '@/dicts/qualify';
import {ENV} from '@/modules/Qualify/common/env';
import {isVirtualUser} from './vcp';

export function getParamStr(url: string, params: any) {
    if (!params) {
        return url;
    }
    const paramStr = Object.keys(params).reduce((prev, cur) => {
        const tag = prev ? '&' : '';
        let val = prev;
        val += `${tag}${cur}=${params[cur]}`;
        return val;
    }, '');
    if (!paramStr) {
        return url;
    }
    return url.includes('?') ? url + '&' + paramStr : url + '?' + paramStr;
}


export function getImageInfo(picture: string, license?: boolean) {
    if (!picture) {
        return '';
    }
    return license ? LICENSE_IMG_URL + picture : IMAGE_URL + picture;
}

export function getImageFileName(corporationPicture: any[]): string {
    const url = corporationPicture && corporationPicture.length ? corporationPicture[0].url : '';
    if (url.includes('filename=')) {
        const seg = url.split('filename=');
        if (seg && seg.length) {
            return seg[1];
        }
    }
    return url;
}

export function getImageFileArr(corporationPicture: any[]): string[] {
    if (!corporationPicture?.length) {
        return [];
    }
    const urls = corporationPicture.map(cur => {
        if (cur?.url?.includes('filename=')) {
            const seg = cur?.url?.split('filename=');
            if (seg && seg.length) {
                return seg[1];
            }
        }
        return cur.url;
    }).filter(cur => !!cur);
    return urls;
}

export function protectText(value: string, start: number, end: number) {
    const str = value || '';
    const values = str.split('');
    let result = '';

    for (let i = 0, len = values.length; i < len; i++) {
        result += i >= start && i <= end ? '*' : values[i];
    }

    return result;
}

/**
 * 字符串脱敏，保留前后retainLength位，中间用maskLength个*代替
 * 如果字符串中已包含*，则不再脱敏
 * @param str 原字符串
 * @param retainLength 前后保留的位数，默认4
 * @param maskLength 中间*的位数，默认4
 * @returns 脱敏后的字符串
 */
export function maskString(str: string, retainLength: number = 4, maskLength: number = 4): string {
    if (!str) {
        return '';
    }
    // 如果已包含*，说明已脱敏，直接返回
    if (str.includes('*')) {
        return str;
    }
    const len = str.length;
    if (len <= retainLength * 2) {
        return str;
    }
    const prefix = str.slice(0, retainLength);
    const suffix = str.slice(-retainLength);
    const mask = '*'.repeat(maskLength);
    return `${prefix}${mask}${suffix}`;
}

export function getParams(search: string) {
    const params: any = {};
    if (search) {
        search.split('&').forEach(cur => {
            const arr = cur.split('=');
            params[arr[0]] = arr[1];
        });
    }
    return params;
}

export function getUrlWithQuery(
    url: string,
    params: any
) {
    const paramStr = Object.keys(params).reduce((prev, cur, i, arr) => {
        const part = i === arr.length - 1 ? `${cur}=${params[cur]}` : `${cur}=${params[cur]}&`;
        return prev + part;
    }, '');
    return `${url}?${paramStr}`;
}

export function isMobileUserAgent() {
    const ua = navigator?.userAgent?.toLowerCase();
    return !!(ua && /mobile|android|iphone|ipod|phone|ipad/i.exec(ua));
}

export const IDENTITY_REG = /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/;

export function getIDCardRule(certificateType?: CERTIFICATE) {
    switch (certificateType) {
        case 'MAINLAND':
            return {
                pattern: IDENTITY_REG, message: '身份证件号码格式有误',
            };
        default:
            return {};
    }
}

export const PERSON_NUMBER_RULE = {
    validator: (rule: any, value: string) => {
        if (IDENTITY_REG.test(value)) {
            const birth = dayjs(value.substring(6, 10), 'YYYY');
            const birthDate = dayjs(value.substring(10, 14), 'MM-DD').format('MM-DD');
            const now = dayjs();
            const date = dayjs().format('MM-DD');
            const gap = now.diff(birth, 'year');
            const smallThanBirthDate = dayjs(birthDate).isAfter(date);
            const isSame = dayjs(date).isSame(birthDate);
            let below14 = now.diff(birth, 'year') < 14;
            if (gap === 14 && (smallThanBirthDate || isSame)) {
                below14 = true;
            }
            return below14
                ? Promise.reject(Error('法律法规要求，不得向未满14周岁的未成年人提供产品和服务，请在父母或其他监护人的指导下向我们提供信息。'))
                : Promise.resolve();
        }
        return Promise.resolve();
    },
};

/**
 * 获取是否变更阶段相关参数
 * @param current
 * @param change
 * @returns
 */
export function getChangeParams(
    current: ChangeStateKey | undefined,
    change: ChangeTypeKey | undefined
) {
    return {
        qualifyChangeState: current ? CHANGE_STATE[current] : QUALIFY_CHANGE_STATE.NONE,
        qualifyChangeType: change ? CHANGE_TYPE[change] : undefined,
    };
}


export function openNewPage(url: string) {
    return () => {
        const canIUseClose = boxx?.canIUse('easybrowse.open');
        if (ENV.baidu() && canIUseClose) {
            return boxx.search.open({
                url,
            });
        }
        const tab = window.open();
        if (tab) {
            tab.location = url;
            tab.opener = null;
        }
    };
}

/**
 * 用户中心移动端未兼容
 */
export function getUserOutletByDevice() {
    if (isMobileUserAgent()) {
        return HOME_PAGE;
    }
    return USER_CENTER;
}


export function openInSamePage(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.click();
}

/**
 * 下载文件
 * @param url 文件链接
 * @param filename 下载文件名
 */
export function downloadFile(url: string, filename?: string) {
    const link = document.createElement('a');
    link.href = url;
    if (filename) {
        link.download = filename;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function isSandbox() {
    return location.host.includes('bcetest')
        || location.host.includes('localhost')
        || location.host.includes(':8100')
        || location.host.includes('console-vcp.baidu-int');
}

export function getH5TicketUrl() {
    return isSandbox() ? H5_TICKET_URL.sandbox : H5_TICKET_URL.online;
}

export function getTicketUrl() {
    return /bce.baidu/.exec(location?.hostname)
        ? (isMobileUserAgent() ? getH5TicketUrl() : `https://ticket.bce.baidu.com${PC_TICKET_CREATE}`)
        : (isMobileUserAgent() ? getH5TicketUrl() : `https://ticket.bcetest.baidu.com${PC_TICKET_CREATE}`);
}

export function isChineseOnly(str?: string) {
    if (!str) {
        return false;
    }
    // 匹配除了空格和换行符之外的中文字符
    const chineseRegex = /^[\u4E00-\u9FFF\r\n\t\s]+$/;
    return chineseRegex.test(str);
}

export function trimPersonName(str?: string) {
    if (!str) {
        return '';
    }
    return isChineseOnly(str) ? str?.replace(/\s/g, '') : str?.trim();
}
// 统一社会信用代码正则
export const newCompNumberReg = /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/;

const SANDBOX_ESCAPE = ['fan', 'token_baidu_test', 'payment_test'];
export function validateCompNumber(num: string, corporationType: ORGANIZATION_TYPE) {
    if (!num) {
        return '';
    }
    // 沙盒环境的Mock兼容
    if (isSandbox() && SANDBOX_ESCAPE.some(cur => num?.includes(cur))) {
        return '';
    }
    if (corporationType === ORGANIZATION_TYPE.DEPARTMENT) {
        return num && /^[0-9A-Z]+$/.test(num?.toLocaleUpperCase()) ? '' : '机构代码只能含有数字和大写字母';
    }
    if (num && newCompNumberReg.test(num?.toLocaleUpperCase())) {
        return '';
    }
    return '您输入的不是有效的统一社会信用代码，长度18位，不含字母I、O、Z、S、V';
}

/**
 * 判断当前页面是否通过iframe加载展示iframe弹窗类型用
 * 虚商也有可能iframe嵌入，所以需要排除
 * @returns 如果当前页面是通过iframe加载的，返回true；否则返回false。
 */
export function isLoadByIframe() {
    return window?.self !== window?.top && !isVirtualUser();
}

// PC页面
export function isLoadByPC() {
    return !isMobileUserAgent() && !isLoadByIframe();
}

export function isSandboxCompNumber(num: string) {
    return isSandbox() && SANDBOX_ESCAPE.some(cur => num.includes(cur));
}

// 沙盒环境的Mock兼容
export function escapeSandboxCompNumber(num: string) {
    if (isSandboxCompNumber(num)) {
        return num;
    }
    return num?.toLocaleUpperCase();
}

/**
 * 验证手机号码或固定电话号码格式是否正确
 *
 * @param str 要验证的字符串
 * @returns 如果字符串符合手机号码或固定电话号码格式，返回true；否则返回false
 */
export function checkPhone(str: string) {
    const mobileRegex = /^1[3-9]\d{9}$/;
    const landlineRegex = /^(0\d{2,3}-?)?\d{7,8}$/;
    return mobileRegex.test(str) || landlineRegex.test(str);
}

/**
 * 验证电话号码是否合法
 * antd form valdiator
 * @returns 返回一个函数，用于验证传入的字符串是否为合法的手机号码或固定电话号码
 */
export function validatePhone(rule: any, value: string) {
    const res = checkPhone(value);
    return res ? Promise.resolve() : Promise.reject(new Error('请输入正确的手机号码或固定电话号码'));
}

/**
 * 获取cdn图标
 * @param name
 * @returns
 */
export function getCDNIcon(name: string) {
    return `https://bcecdn.bj.bcebos.com/eop-fe/risk/qualify/${name}`;
}

/**
 * 姓名脱敏，只展示最后一位，其余使用*替代
 * @param name 姓名
 * @returns 脱敏后的姓名
 */
export function maskName(name?: string): string {
    if (!name) {
        return '';
    }
    // 如果已包含*，说明已脱敏，直接返回
    if (name.includes('*')) {
        return name;
    }
    if (name.length <= 1) {
        return name;
    }
    return '*'.repeat(name.length - 1) + name.slice(-1);
}
